import type {
  ContentFeedEntryListResponse,
  ContentFeedSearchRequest,
} from '@sola/schema/dotcom/proxy-api/content-feed.ts';
import type { ErrorResponse } from '@sola/shared-utils/httpClient.ts';
import {
  httpPostWithStatus,
  isErrorResponse,
  isSuccessStatus,
} from '@sola/shared-utils/httpClient.ts';

import { MY_API_BASE } from '../constants.ts';

// Trailing slash is intentional: the My app runs with `trailingSlash` enabled,
// so `/content-feed/entries` 308-redirects to `/content-feed/entries/`. Calling
// the slashed path directly avoids a redirect that can drop the POST body and
// the signed `x-amz-content-sha256` header.
export const routeContentFeedEntries = () =>
  `${MY_API_BASE}/content-feed/entries/`;

export interface FetchContentFeedEntriesParams {
  cursor?: string | null;
  locale: string;
  limit?: number;
  programCountryIsoCode?: string;
  programCountryIsoCodes?: string[];
  participantLocalIds?: string[];
  fcpIds?: string[];
  includeNearbyEntries?: boolean;
  sortBy?: 'publishedAt' | 'proximity';
  sortDirection?: 'asc' | 'desc';
}

/**
 * Searches content feed entries via the `my` app's Next.js API route, which
 * proxies to the internal proxy-api content-feed search endpoint.
 *
 * Uses httpPostWithStatus with signRequest so the `x-amz-content-sha256` body
 * hash header is attached — required because the `my` server sits behind
 * CloudFront OAC with SigV4 signing to a private (AWS_IAM) Lambda function URL,
 * which rejects unsigned POST bodies with a signature mismatch error.
 */
export const fetchContentFeedEntries = async (
  params: FetchContentFeedEntriesParams,
): Promise<ContentFeedEntryListResponse> => {
  const body: Partial<ContentFeedSearchRequest> = {
    // Hardcoded to 'en' — we only support English content in the feed today.
    // When we add multi-locale support, map params.locale (e.g. 'en-US' → 'en')
    // and pass it here so the i18n.language re-fetch in useContentFeed takes effect.
    locale: 'en',
  };

  if (params.cursor) {
    body.cursor = params.cursor;
  }

  if (params.limit) {
    body.limit = params.limit;
  }

  if (params.programCountryIsoCodes?.length) {
    body.programCountryIsoCodes = params.programCountryIsoCodes;
  } else if (params.programCountryIsoCode) {
    body.programCountryIsoCodes = [params.programCountryIsoCode];
  }

  if (params.participantLocalIds?.length) {
    body.participantLocalIds = params.participantLocalIds;
  }

  if (params.fcpIds?.length) {
    body.fcpIds = params.fcpIds;
  }

  if (params.includeNearbyEntries != null) {
    body.includeNearbyEntries = params.includeNearbyEntries;
  }

  if (params.sortBy) {
    body.sortBy = params.sortBy;
  }

  if (params.sortDirection) {
    body.sortDirection = params.sortDirection;
  }

  const { response, statusCode } = await httpPostWithStatus<
    ContentFeedEntryListResponse | ErrorResponse
  >(routeContentFeedEntries(), body, {
    signRequest: true,
  });

  if (!isSuccessStatus(statusCode) || !response || isErrorResponse(response)) {
    throw new Error(`Content feed API error: ${statusCode}`);
  }

  return response;
};
